import React, { useMemo } from "react";

import { Inline, xcss } from "@atlaskit/primitives-next";
import Spinner from "@atlaskit/spinner";

import { useFetchSupportRequests } from "@/components/fastshift/src/common/hooks";
import { useFastshiftSelector } from "@/components/fastshift/src/state";

import { USER_TYPE } from "../../constants";
import { CustomerRelationshipsSection } from "../../relationships/src/sections/customer-relationships-section";
import { InternalRelationshipsSection } from "../../relationships/src/sections/internal-relationships-section";
import { NoRelationshipsSection } from "../../relationships/src/sections/no-relationships-section";
import { PartnerRelationshipsSection } from "../../relationships/src/sections/partner-relationships-section";

import { useRelationshipsPageAnalytics } from "./hooks";
import { RawRelationships } from "./types";

const loadingWrapperStyles = xcss({
    padding: "space.500"
});

const renderSectionByUserType = ({
    userType,
    relationships
}: {
    userType: string;
    relationships: RawRelationships;
}) => {
    switch (userType) {
        case USER_TYPE.CUSTOMER:
            return (
                <CustomerRelationshipsSection relationships={relationships} />
            );
        case USER_TYPE.PARTNER:
            return (
                <PartnerRelationshipsSection relationships={relationships} />
            );
        case USER_TYPE.INTERNAL:
            return (
                <InternalRelationshipsSection relationships={relationships} />
            );
        default:
            return <NoRelationshipsSection />;
    }
};

export const RelationshipSection: React.FC = () => {
    const { pending: customerDomainPending, data: customerDomainData } =
        useFastshiftSelector((state) => state.customerDomain);
    const { pending: userDetailsPending, data: userDetailsData } =
        useFastshiftSelector((state) => state.userDetails);

    const relationships = useMemo(
        () => customerDomainData?.relationships || [],
        [customerDomainData?.relationships]
    );

    // Fetch support requests data for RequestAssistanceModal
    useFetchSupportRequests();

    // Use the page analytics hook (auto-fires section display event)
    useRelationshipsPageAnalytics(relationships);

    if (customerDomainPending || userDetailsPending) {
        return (
            <Inline
                alignBlock="center"
                alignInline="center"
                xcss={loadingWrapperStyles}
            >
                <Spinner
                    testId="spinner"
                    interactionName="load"
                    label="Loading"
                    size="medium"
                />
            </Inline>
        );
    }

    // No staffs assigned to this customer domain
    if (relationships.length === 0) {
        return <NoRelationshipsSection />;
    }

    return renderSectionByUserType({
        userType: userDetailsData?.userType || "",
        relationships
    });
};
